/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Select } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Sla } from "~/models";
import { useAxios } from "~/tools";
import { PreviewContent } from "./PreviewContent";
import { SelectPreviewItem } from "./SelectPreviewItem";

type Option = {
  label: string;
  value: string;
};

type ServicePreviewProps = {
  filteredServices: Option[];
  getValue: () => Option[];
  inputId?: string;
  setSelectedId: React.Dispatch<React.SetStateAction<string>>;
};

const selectedServiceInitialState = {
  name: "",
  value: "",
};

function ServicePreview({
  inputId,
  filteredServices,
  getValue,
  setSelectedId,
}: ServicePreviewProps) {
  const [isLoading, setIsLoading] = React.useState(true);

  const [sla, setSla] = React.useState<Sla[]>([]);
  const [selectedService, setSelectedService] = React.useState(
    selectedServiceInitialState
  );

  const { Axios } = useAxios();
  const { showError } = useShowError();

  const fetchSlas = React.useCallback(async () => {
    const serviceId = selectedService.value;
    try {
      const { data } = await Axios().get(`/catalog/services/${serviceId}/slas`);

      setSla(data.slas);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, selectedService.value, showError]);

  React.useEffect(() => {
    selectedService.value?.length !== 0 && fetchSlas();
  }, [selectedService.value?.length, fetchSlas]);

  return (
    <>
      <Select
        inputId={inputId}
        components={{
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Option: (props) => (
            // @ts-expect-error there is a bug with type generation in GNUI
            <SelectPreviewItem
              {...props}
              setSelectedService={setSelectedService}
            />
          ),
        }}
        // @ts-expect-error there is a bug with type generation in GNUI
        onChange={(g: Option) => {
          setSelectedId(g?.value);
        }}
        value={getValue()}
        options={filteredServices}
      />
      <PreviewContent
        sla={sla}
        selectedService={selectedService}
        isLoading={isLoading}
      />
    </>
  );
}

export { ServicePreview };
